import { Link, useNavigate, useLocation } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { useEmailVerification } from "@shared";
import { useAuthRedirect } from "@shared";
import {
  signUpSchema,
  getFieldSchema,
  getConfirmPasswordSchema,
  type SignUpForm,
} from "@shared";
import { Form, FormField, Button, Flex, Container, Text, Separator, Alert, GoogleIcon } from '@printtat/ui';
import { Loader2 } from 'lucide-react';
import { TurnstileWidget } from '@/shared/components/TurnstileWidget';
import { TURNSTILE_SITE_KEY } from '@/lib/turnstile';

const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// API function for registration
const registerUser = async (
  data: Omit<SignUpForm, 'confirmPassword'> & { turnstileToken?: string },
) => {
  const { turnstileToken, ...payload } = data;
  const response = await fetch(`${apiUrl}/auth/sign-up`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      ...payload,
      // The backend's TurnstileGuard reads this field off the request body
      // and strips it before validation, so it never reaches SignUpDto.
      ...(turnstileToken ? { 'cf-turnstile-response': turnstileToken } : {}),
    }),
  });

  console.log("Response received:", response.status, response.statusText);

  if (!response.ok) {
    const errorData = await response.json();
    console.error("Backend error:", errorData);
    throw new Error(errorData.message || "Registration failed");
  }

  const result = await response.json();
  console.log("Backend success response:", result);
  return result;
};

// API function for Google OAuth
const initiateGoogleOAuth = () => {
  window.location.href = `${apiUrl}/auth/google`;
};

export const SignUpPage = () => {
  const { loading } = useAuthRedirect(); // This will redirect to dashboard if already authenticated
  const navigate = useNavigate();
  const location = useLocation();
  const { showVerificationOverlayHandler } = useEmailVerification();
  const [error, setError] = useState<string | null>(null);
  const [showGoogleError, setShowGoogleError] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string>('');

  // Handle error state from navigation (e.g., from OAuth callback)
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
      if (location.state.showGoogleError) {
        setShowGoogleError(true);
      }
      // Clear the state to prevent showing the error again on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, navigate, location.pathname]);

  const registerMutation = useMutation({
    mutationFn: registerUser,
    onSuccess: (data) => {
      console.log("Registration successful:", data);
      setError(null);

      // Account is unverified — backend intentionally does NOT return a JWT
      // here. The user becomes signed-in only after clicking the verification
      // link, which lands on /auth/callback?verification_token=… and mints
      // the JWT there. Land them on /sign-in with a "check your email" hint.
      const email = data.user?.email || "";
      showVerificationOverlayHandler(email);
      navigate("/sign-in", {
        replace: true,
        state: {
          info:
            data.message ||
            "Account created. Check your email for the verification link to finish signing in.",
          pendingVerificationEmail: email,
        },
      });
    },
    onError: (error: Error) => {
      console.error("Registration failed:", error);
      if (error.message?.includes("already exists")) {
        setError(
          "An account with this email already exists. Please sign in instead."
        );
      } else {
        setError(error.message || "Registration failed. Please try again.");
      }
    },
  });

  const handleSubmit = async (data: SignUpForm) => {
    // Block submission if Turnstile is configured but hasn't returned a token.
    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the captcha before submitting.');
      return;
    }
    registerMutation.mutate({
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      password: data.password,
      turnstileToken,
    });
  };

  // Show loading while checking authentication
  if (loading) {
    return (
      <Container size="2" className="min-h-screen">
        <Flex align="center" justify="center" className="min-h-screen">
          <Flex direction="column" align="center" gap="4">
            <Loader2 className="h-12 w-12 animate-spin" />
            <Text size="3" color="gray">Checking authentication...</Text>
          </Flex>
        </Flex>
      </Container>
    );
  }

  return (
    <Container size="2" className="min-h-screen">
      <Flex align="center" justify="center" className="min-h-screen">
        <Flex direction="column" gap="6" className="w-full max-w-sm">
          <Flex direction="column" align="center" gap="2">
            <Text size="8" weight="bold" align="center">
              Create your account
            </Text>
            <Text size="2" color="gray" align="center">
              Already have an account? {" "}
              <Link
                to="/sign-in"
                className="text-blue-600 hover:text-blue-700 font-medium no-underline"
              >
                Sign in
              </Link>
            </Text>
          </Flex>

          <Form
            schema={signUpSchema}
            defaultValues={{
              firstName: "",
              lastName: "",
              email: "",
              password: "",
              confirmPassword: "",
            }}
            onSubmit={handleSubmit}
            submitText="Sign Up"
            loadingText="Creating account..."
          >
            {(form) => (
              <Flex direction="column" gap="4">
                <Flex gap="4" className="flex-row">
                  <div className="flex-1">
                    <FormField
                      form={form}
                      name="firstName"
                      label="First name"
                      type="text"
                      autoComplete="given-name"
                      required
                      placeholder="First name"
                      validationSchema={getFieldSchema(
                        signUpSchema,
                        "firstName"
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <FormField
                      form={form}
                      name="lastName"
                      label="Last name"
                      type="text"
                      autoComplete="family-name"
                      required
                      placeholder="Last name"
                      validationSchema={getFieldSchema(
                        signUpSchema,
                        "lastName"
                      )}
                    />
                  </div>
                </Flex>

                <FormField
                  form={form}
                  name="email"
                  label="Email address"
                  type="email"
                  autoComplete="email"
                  required
                  placeholder="Email address"
                  validationSchema={getFieldSchema(signUpSchema, "email")}
                />

                <FormField
                  form={form}
                  name="password"
                  label="Password"
                  type="password"
                  autoComplete="new-password"
                  required
                  placeholder="Password"
                  validationSchema={getFieldSchema(signUpSchema, "password")}
                />

                <FormField
                  form={form}
                  name="confirmPassword"
                  label="Confirm password"
                  type="password"
                  autoComplete="new-password"
                  required
                  placeholder="Confirm password"
                  validationSchema={() => getConfirmPasswordSchema(form.getFieldValue('password'))}
                />

                <TurnstileWidget onChange={setTurnstileToken} />

                {error && (
                  <Alert variant="error">
                    <Text size="2" className="mb-2">{error}</Text>
                    {showGoogleError && (
                      <Flex direction="column" gap="2">
                        <Text size="2">You can:</Text>
                        <Flex direction="column" gap="1" className="pl-4">
                          <Text size="2">
                            <Link
                              to="/sign-in"
                              className="text-blue-600 hover:text-blue-700 underline"
                            >
                              Sign in with your password
                            </Link>
                          </Text>
                          <Text size="2" color="gray">
                            Try signing up with a different Google account
                          </Text>
                          <Text size="2" color="gray">
                            Use a different email address
                          </Text>
                        </Flex>
                      </Flex>
                    )}
                    {error.includes("already exists") && !showGoogleError && (
                      <Text size="2" className="mt-2">
                        <Link
                          to="/sign-in"
                          className="text-blue-600 hover:text-blue-700 underline"
                        >
                          Sign in to your account
                        </Link>
                      </Text>
                    )}
                  </Alert>
                )}
              </Flex>
            )}
          </Form>

          <Flex direction="column" gap="4">
            <Separator size="4" />
            
            <Flex align="center" justify="center">
              <Text size="2" color="gray">Alternative sign up methods</Text>
            </Flex>

            <Button
              variant="outline"
              onClick={initiateGoogleOAuth}
              className="w-full"
            >
              <GoogleIcon size={20} />
              <Text className="ml-2">Sign up with Google</Text>
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Container>
  );
};
